<template>
  <div class="workflow-page">
    <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav">
        <b-navbar-toggle target="workflow_nav"></b-navbar-toggle>
        <b-collapse is-nav id="workflow_nav">
          <b-navbar-nav>
            <b-nav-item>
              <b-button size="sm" @click="showCreate">new workflow</b-button>
            </b-nav-item>
            <li v-if="activeWorkflow" class="nav-item workflow-nav-action">
              <button type="button" class="workflow-delete" @click="deleteVisible = true">delete</button>
            </li>
            <b-nav-item>
              <modal-help topic="workflow-editor" label="Help for this screen" show-label />
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    <div class="workflow-body">
      <aside class="workflow-sidebar">
          <div class="workflow-sidebar-label">Workflows</div>
          <button
            v-for="workflow in workflows"
            :key="workflow.uniqueTitle"
            type="button"
            class="workflow-sidebar-item"
            :class="{ active: activeWorkflow && activeWorkflow.uniqueTitle === workflow.uniqueTitle }"
            @click="choose(workflow)"
          >
            <font-awesome-icon icon="code-branch" class="workflow-sidebar-icon" />
            <span>{{ workflow.title || workflow.uniqueTitle }}</span>
          </button>
          <div v-if="!workflows.length" class="workflow-sidebar-empty">No workflows yet</div>
      </aside>
      <section v-if="activeWorkflow" class="workflow-main">
        <div class="workflow-editor">
        <div class="workflow-editor-inner">
          <div class="workflow-page-settings">
            <label class="workflow-page-field">
              Title
              <input v-model="activeWorkflow.title" class="workflow-page-input" type="text" />
            </label>
            <label class="workflow-page-field">
              Starting spreadsheet
              <select v-model="activeWorkflow.spreadSheetUniqueTitle" class="workflow-page-input">
                <option v-for="sheet in spreadsheets" :key="sheet.uniqueTitle" :value="sheet.uniqueTitle">{{ sheet.title || sheet.uniqueTitle }}</option>
              </select>
            </label>
          </div>
          <p class="workflow-page-note">Each run starts from one record on this spreadsheet. A form can pass the submitted record, or one record linked to it.</p>
          <label class="workflow-page-field">
            Description
            <textarea v-model="activeWorkflow.description" class="workflow-page-input workflow-page-textarea" rows="2"></textarea>
          </label>
          <h2 class="workflow-page-heading">Steps</h2>
          <workflow-steps
            :steps="activeWorkflow.steps"
            :spreadsheets="spreadsheets"
            :automations="automations"
            :variable-names="variableNames"
            :workflow-sheet="activeWorkflow.spreadSheetUniqueTitle"
            @change="dirty = true"
          />
          <section class="workflow-run">
            <h2 class="workflow-page-heading">Run on a record</h2>
            <p class="workflow-page-note">Uses the saved workflow. Enter the id of one record on the starting spreadsheet.</p>
            <div class="workflow-run-row">
              <input v-model="runRecordId" class="workflow-page-input workflow-record-id" type="number" min="1" placeholder="Record id" />
              <button type="button" class="workflow-primary" :disabled="running" @click="saveAndRun">{{ running ? 'Running' : 'Save and run' }}</button>
            </div>
            <p v-if="runError" class="workflow-error">{{ runError }}</p>
            <p v-else-if="runMessage" class="workflow-saved">{{ runMessage }}</p>
            <ol v-if="runSteps.length" class="workflow-run-steps">
              <li v-for="(step, index) in runSteps" :key="index" :class="'workflow-run-' + step.status">
                {{ step.type }}<template v-if="step.detail"> — {{ step.detail }}</template>
              </li>
            </ol>
          </section>
          <section class="workflow-runs">
            <div class="workflow-runs-head">
              <h2 class="workflow-page-heading">Runs</h2>
              <button type="button" class="workflow-text-btn" @click="loadLogs">Refresh</button>
            </div>
            <p v-if="logsLoading" class="workflow-page-note">Loading runs…</p>
            <p v-else-if="!logs.length" class="workflow-page-note">No runs yet.</p>
            <button
              v-for="entry in logs"
              :key="entry._id"
              type="button"
              class="workflow-log"
              :class="{ active: selectedLog && selectedLog._id === entry._id }"
              @click="selectedLog = entry"
            >
              <span :class="'workflow-run-' + entry.status">{{ entry.status }}</span>
              <span>record {{ entry.recordId == null ? '—' : entry.recordId }}</span>
              <span>{{ entry.trigger }}</span>
              <span>{{ formatWhen(entry.finishedAt) }}</span>
              <span v-if="entry.message">{{ entry.message }}</span>
            </button>
            <ol v-if="selectedLog && selectedLog.steps && selectedLog.steps.length" class="workflow-run-steps">
              <li v-for="(step, index) in selectedLog.steps" :key="index" :class="'workflow-run-' + step.status">
                {{ step.type }}<template v-if="step.detail"> — {{ step.detail }}</template>
              </li>
            </ol>
          </section>
        </div>
        </div>
        <div class="workflow-save-bar">
          <button type="button" class="workflow-primary" :disabled="saving" @click="save">{{ saving ? 'Saving' : 'Save' }}</button>
          <span v-if="saveError" class="workflow-error">{{ saveError }}</span>
          <span v-else-if="saved" class="workflow-saved">Saved</span>
        </div>
      </section>
      <section v-else class="workflow-main">
        <p class="workflow-page-note workflow-editor-inner">Choose a workflow, or create one. A workflow is a sequence of record updates, queries, automations, and branches that starts from a single record.</p>
      </section>
    </div>

    <b-modal v-model="createVisible" title="create workflow" @ok="create">
      <template #title>
        create workflow
        <modal-help topic="create-workflow" />
      </template>
      <b-form-group label="title">
        <b-form-input v-model="newTitle" @input="onTitleInput" />
      </b-form-group>
      <b-form-group label="unique title">
        <b-form-input v-model="newUniqueTitle" @input="uniqueTitleEdited = true" />
      </b-form-group>
      <b-form-group label="starting spreadsheet">
        <b-form-select v-model="newSheet" :options="sheetOptions" />
      </b-form-group>
      <p v-if="createError" class="workflow-error">{{ createError }}</p>
    </b-modal>

    <b-modal v-model="deleteVisible" title="delete workflow" ok-variant="danger" ok-title="delete" @ok="remove">
      <template #title>
        delete workflow
        <modal-help topic="delete-workflow" />
      </template>
      <p v-if="activeWorkflow">Delete <strong>{{ activeWorkflow.title }}</strong> ({{ activeWorkflow.uniqueTitle }})? Forms and schedules that still name it will not find it the next time they run.</p>
    </b-modal>
  </div>
</template>

<script>
import WorkflowSteps from '../lib/WorkflowSteps.vue'
import { collectVariableNames } from '../../../../shared/step-workflow.js'
import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'

export default {
  name: 'StepWorkflows',
  components: { WorkflowSteps },
  data () {
    return {
      workflows: [],
      spreadsheets: [],
      automations: [],
      activeWorkflow: null,
      dirty: false,
      saving: false,
      saved: false,
      saveError: '',
      createVisible: false,
      deleteVisible: false,
      newTitle: '',
      newUniqueTitle: '',
      uniqueTitleEdited: false,
      newSheet: '',
      createError: '',
      runRecordId: '',
      running: false,
      runError: '',
      runSteps: [],
      runMessage: '',
      logs: [],
      logsLoading: false,
      selectedLog: null
    }
  },
  computed: {
    variableNames () {
      if (!this.activeWorkflow) return []
      return collectVariableNames(this.activeWorkflow.steps || [])
    },
    sheetOptions () {
      return [{ value: '', text: 'Choose a spreadsheet' }].concat((this.spreadsheets || []).map((sheet) => ({
        value: sheet.uniqueTitle,
        text: sheet.title || sheet.uniqueTitle
      })))
    }
  },
  watch: {
    '$route.params.workflow' () {
      this.selectFromRoute()
    },
    '$route.params.application' () {
      this.load()
    }
  },
  mounted () {
    this.load()
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {
    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  methods: {
    async refreshWorkflowListKeepingActive () {
      const headers = this.headers()
      const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/`, { headers })
      const id = this.activeWorkflow && this.activeWorkflow.uniqueTitle
      const rows = response.data || []
      this.workflows = rows.map((workflow) => workflow.uniqueTitle === id ? this.activeWorkflow : workflow)
      if (id && !this.workflows.some((workflow) => workflow.uniqueTitle === id)) {
        this.workflows.push(this.activeWorkflow)
      }
    },
    async applyDesignerChange (change) {
      const id = this.activeWorkflow && this.activeWorkflow.uniqueTitle
      const keys = change && change.keys || []
      const hitsActive = !!(id && (keys.includes(`workflow:${id}`) || keys.includes('application')))
      const hitsList = keys.includes('workflows') || keys.includes('application') || keys.some((key) => key.startsWith('workflow:'))
      if (!hitsList && !hitsActive) return
      if (this.dirty && hitsActive) {
        raiseDesignerWarning(change, keys.includes('application') ? 'this application' : 'this workflow')
        try {
          await this.refreshWorkflowListKeepingActive()
        } catch (error) {
          this.saveError = 'Could not load workflows'
        }
        return
      }
      if (this.dirty) {
        try {
          await this.refreshWorkflowListKeepingActive()
        } catch (error) {
          this.saveError = 'Could not load workflows'
        }
        return
      }
      await this.load()
    },
    headers () {
      return { 'application-unique-title': this.$route.params.application }
    },
    async load () {
      const headers = this.headers()
      try {
        const [workflows, spreadsheets, automations] = await Promise.all([
          this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/`, { headers }),
          this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`, { headers }),
          this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/automations/`, { headers })
        ])
        this.workflows = workflows.data || []
        this.spreadsheets = spreadsheets.data || []
        this.automations = automations.data || []
        this.selectFromRoute()
      } catch (error) {
        this.saveError = 'Could not load workflows'
      }
    },
    selectFromRoute () {
      const uniqueTitle = this.$route.params.workflow
      if (!uniqueTitle) {
        this.activeWorkflow = null
        return
      }
      const workflow = this.workflows.find((item) => item.uniqueTitle === uniqueTitle)
      if (workflow) this.choose(workflow, false)
    },
    choose (workflow, updateRoute) {
      this.activeWorkflow = workflow
      if (!Array.isArray(this.activeWorkflow.steps)) this.activeWorkflow.steps = []
      this.dirty = false
      this.saved = false
      this.saveError = ''
      this.runSteps = []
      this.runError = ''
      this.runMessage = ''
      this.selectedLog = null
      this.loadLogs()
      const target = `/${this.$route.params.application}/workflows/${workflow.uniqueTitle}`
      if (updateRoute !== false && this.$route.path !== target) this.$router.replace(target)
    },
    showCreate () {
      this.newTitle = ''
      this.newUniqueTitle = ''
      this.uniqueTitleEdited = false
      this.newSheet = this.spreadsheets[0] ? this.spreadsheets[0].uniqueTitle : ''
      this.createError = ''
      this.createVisible = true
    },
    onTitleInput () {
      if (this.uniqueTitleEdited) return
      this.newUniqueTitle = String(this.newTitle || '').toLowerCase().replace(/[^a-z0-9\s_]/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
    },
    async create (event) {
      if (!this.newTitle || !this.newUniqueTitle || !this.newSheet) {
        if (event && event.preventDefault) event.preventDefault()
        this.createError = 'Title, unique title, and spreadsheet are required'
        return
      }
      try {
        await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/`, {
          title: this.newTitle,
          uniqueTitle: this.newUniqueTitle,
          spreadSheetUniqueTitle: this.newSheet
        }, { headers: this.headers() })
        await this.load()
        const created = this.workflows.find((item) => item.uniqueTitle === this.newUniqueTitle)
        if (created) this.choose(created)
      } catch (error) {
        if (event && event.preventDefault) event.preventDefault()
        const message = error && error.response && error.response.data
        this.createError = typeof message === 'string' ? message : 'Could not create the workflow'
      }
    },
    async save () {
      if (!this.activeWorkflow) return false
      this.saving = true
      this.saveError = ''
      this.saved = false
      try {
        const response = await this.$http.put(
          `${VUE_APP_PROXY_SERVER_URL}/step-workflows/${this.activeWorkflow.uniqueTitle}`,
          {
            title: this.activeWorkflow.title,
            description: this.activeWorkflow.description || '',
            spreadSheetUniqueTitle: this.activeWorkflow.spreadSheetUniqueTitle,
            steps: this.activeWorkflow.steps || []
          },
          { headers: this.headers() }
        )
        const saved = response.data
        const index = this.workflows.findIndex((item) => item.uniqueTitle === saved.uniqueTitle)
        if (index >= 0) this.workflows.splice(index, 1, saved)
        this.activeWorkflow = saved
        this.saved = true
        this.dirty = false
        this.saving = false
        return true
      } catch (error) {
        const message = error && error.response && error.response.data
        this.saveError = typeof message === 'string' ? message : 'Could not save the workflow'
        this.saving = false
        return false
      }
    },
    async saveAndRun () {
      this.runError = ''
      this.runMessage = ''
      this.runSteps = []
      const saved = await this.save()
      if (!saved) return
      if (!this.runRecordId) {
        this.runError = 'Enter a record id'
        return
      }
      this.running = true
      try {
        const response = await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/step-workflows/${this.activeWorkflow.uniqueTitle}/execute`,
          {
            recordId: this.runRecordId,
            recordSpreadSheet: this.activeWorkflow.spreadSheetUniqueTitle,
            recordSource: 'form'
          },
          { headers: this.headers() }
        )
        const result = response.data || {}
        const run = (result.runs && result.runs[0]) || result
        this.runSteps = run.steps || []
        if (result.status === 'error' || run.status === 'error') this.runError = result.error || run.error || 'The workflow failed'
        else if (run.status === 'stopped') this.runMessage = run.message || 'Stopped'
        await this.loadLogs()
      } catch (error) {
        const message = error && error.response && error.response.data
        this.runError = typeof message === 'string' ? message : 'The workflow failed'
      }
      this.running = false
    },
    formatWhen (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString()
    },
    async loadLogs () {
      if (!this.activeWorkflow) {
        this.logs = []
        return
      }
      this.logsLoading = true
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/step-workflows/${this.activeWorkflow.uniqueTitle}/logs`,
          { headers: this.headers() }
        )
        this.logs = response.data || []
        if (this.selectedLog) {
          this.selectedLog = this.logs.find((entry) => entry._id === this.selectedLog._id) || null
        }
      } catch (error) {
        this.logs = []
      }
      this.logsLoading = false
    },
    async remove (event) {
      if (event && event.preventDefault) event.preventDefault()
      if (!this.activeWorkflow) return
      const uniqueTitle = this.activeWorkflow.uniqueTitle
      try {
        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/step-workflows/${uniqueTitle}`,
          { headers: this.headers() }
        )
      } catch (error) {
        const message = error && error.response && error.response.data
        this.saveError = typeof message === 'string' ? message : 'Could not delete the workflow'
        return
      }
      this.deleteVisible = false
      this.activeWorkflow = null
      await this.$router.replace(`/${this.$route.params.application}/workflows`)
      await this.load()
    }
  }
}
</script>

<style scoped>
.workflow-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.workflow-body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.workflow-nav-action {
  display: flex;
  align-items: center;
  padding: 0 8px;
}
button.workflow-delete {
  max-width: none !important;
  min-width: 0 !important;
  width: auto !important;
  flex: 0 0 auto !important;
  color: #f82471;
  border: 1px solid #f82471;
  background: transparent;
  border-radius: 4px;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 4px 10px;
  cursor: pointer;
}
button.workflow-delete:hover {
  background: #f82471;
  color: #fff;
}
.workflow-sidebar {
  flex: 0 0 260px;
  width: 260px;
  background: #141412;
  border-right: 1px solid #2e2e2b;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
.workflow-sidebar-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a8a8a4;
  padding: 14px 14px 8px;
}
.workflow-sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  color: #b0b0ae;
  padding: 7px 14px;
  cursor: pointer;
}
.workflow-sidebar-item span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.workflow-sidebar-item:hover {
  background: #1e1e1b;
  color: #e8e8e6;
}
.workflow-sidebar-empty,
.workflow-page-note {
  color: #8d8d8a;
  font-size: 0.85rem;
  padding: 0 14px 12px;
}
.workflow-editor .workflow-page-note {
  padding-left: 0;
  padding-right: 0;
}
.workflow-main {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.workflow-editor {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
}
.workflow-editor-inner {
  padding: 16px 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.workflow-save-bar {
  display: flex;
  flex: 0 0 auto;
  gap: 10px;
  align-items: center;
  padding: 10px 20px;
  border-top: 1px solid #2e2e2b;
  background: #141412;
}
.workflow-page-settings {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
}
.workflow-page-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  color: #b0b0ae;
  font-size: 0.72rem;
}
.workflow-page-input {
  width: 16rem;
  max-width: 100%;
  height: 30px;
  box-sizing: border-box;
  background: #1c1c19;
  color: #e8e8e6;
  border: 1px solid #3a3a36;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.85rem;
}
.workflow-page-textarea {
  width: 28rem;
  height: auto;
  padding: 6px 8px;
}
.workflow-record-id {
  width: 8rem;
}
.workflow-page-heading {
  font-size: 1rem;
  margin: 8px 0 0;
}
.workflow-save-row,
.workflow-run-row {
  display: flex;
  gap: 10px;
  align-items: center;
}
.workflow-primary {
  background: #9ad12b;
  color: #141412;
  border: 0;
  border-radius: 4px;
  padding: 6px 12px;
  font-weight: 700;
}
.workflow-error {
  color: #e07a7a;
}
.workflow-saved {
  color: #9ad12b;
}
.workflow-run {
  border-top: 1px solid #2e2e2b;
  padding-top: 8px;
}
.workflow-run-steps {
  margin: 0;
  padding-left: 1.2rem;
}
.workflow-runs {
  border-top: 1px solid #2e2e2b;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.workflow-runs-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.workflow-text-btn {
  background: transparent;
  color: #c8c8c4;
  border: 1px solid #3a3a36;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  cursor: pointer;
}
.workflow-log {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  width: 100%;
  text-align: left;
  background: #141412;
  color: #c8c8c4;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  padding: 6px 8px;
  font-size: 0.8rem;
  cursor: pointer;
}
.workflow-log.active {
  border-color: #9ad12b;
}
.workflow-run-error {
  color: #e07a7a;
}
.workflow-run-stopped {
  color: #d2b48c;
}
.workflow-run-success {
  color: #9ad12b;
}
</style>
